/**
 * StageLadder — Phase 3 (earned-progression model)
 *
 * Renders the 11 responsibility stages as a vertical ladder. Stages map to:
 *   • current   — fully open, lessons listed
 *   • unlocked  — past stage, available for refresher (backwards always allowed)
 *   • mastered  — past stage with all lessons completed (subtle ✓)
 *   • awaiting_confirmation — pupil read all lessons, waiting on instructor verdict
 *   • locked-next   — next stage in line, visible but not actionable
 *   • locked-future — distant stage, just a milestone name
 *
 * Tap on a current/unlocked stage → routes to its first uncompleted lesson.
 * Tap on locked → calm message ("Finish X first").
 *
 * Reliability (Apr 30, 2026): never silently render null on load failure.
 * Always show the ladder skeleton; on error show a calm retry pane so the
 * learner can see the system is here, just having a moment.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Lock, Circle, ChevronRight, Sparkles, Hourglass, RefreshCw, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_TONE = {
  current:                 { ring: "ring-amber-400/60", bg: "bg-amber-50",    text: "text-amber-900",  badge: "bg-amber-500 text-white" },
  unlocked:                { ring: "ring-indigo-200",   bg: "bg-white",       text: "text-slate-900",  badge: "bg-indigo-100 text-indigo-700" },
  mastered:                { ring: "ring-emerald-200",  bg: "bg-emerald-50",  text: "text-emerald-900", badge: "bg-emerald-500 text-white" },
  awaiting_confirmation:   { ring: "ring-indigo-300",   bg: "bg-indigo-50",   text: "text-indigo-900", badge: "bg-indigo-500 text-white" },
  "locked-next":           { ring: "ring-slate-200",    bg: "bg-slate-50",    text: "text-slate-500",  badge: "bg-slate-100 text-slate-500" },
  "locked-future":         { ring: "ring-slate-200",    bg: "bg-slate-50/50", text: "text-slate-400",  badge: "bg-slate-100 text-slate-400" },
};

const STATE_LABEL = {
  current:                 "Current stage",
  unlocked:                "Available · refresher",
  mastered:                "Mastered",
  awaiting_confirmation:   "Awaiting instructor confirmation",
  "locked-next":           "Up next · locked",
  "locked-future":         "Locked",
};

export default function StageLadder() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/learner-framework/me`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load your learning stages right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ━━ Reliability shell — visible always, never silently null ━━
  // The Responsibility Learning System is the spine of the learner's
  // experience; if the API hiccups we still want the section to be visible
  // with a calm retry, not invisible.
  const shell = (children) => (
    <div data-testid="stage-ladder" className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">Your responsibility ladder</h3>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          0% → 100% · earned forward · always open back
        </span>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return shell(
      <div data-testid="stage-ladder-loading" className="px-4 py-8 text-center">
        <div className="inline-flex items-center gap-2 text-slate-400 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading your stages…
        </div>
      </div>
    );
  }

  if (error || !data?.stage_ladder?.length) {
    return shell(
      <div data-testid="stage-ladder-error" className="px-4 py-6 text-center space-y-3">
        <div className="inline-flex items-center gap-2 text-amber-600">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-bold">We can't load your learning stages right now</span>
        </div>
        <p className="text-xs text-slate-500 max-w-xs mx-auto">
          The system is here — it's just having a moment. Tap retry and we'll pull your ladder again.
        </p>
        <button
          type="button"
          data-testid="stage-ladder-retry"
          onClick={load}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 active:scale-[0.98] transition"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const ladder = data.stage_ladder;

  return shell(
      <ol className="divide-y divide-slate-100">
        {ladder.map((stage, idx) => {
          const tone = STATE_TONE[stage.state] || STATE_TONE.unlocked;
          const isLocked = stage.state === "locked-next" || stage.state === "locked-future";
          const handleClick = () => {
            if (isLocked) return;
            // Open the framed stage view for Before/Pointers/Reflection wrapping.
            // Stage view shows the lesson list so a single tap into a lesson
            // is still one tap away — but with proper context.
            nav(`/stage/${stage.slug}`);
          };
          const Icon = stage.state === "mastered" ? CheckCircle2
                     : stage.state === "awaiting_confirmation" ? Hourglass
                     : stage.state === "locked-next" || stage.state === "locked-future" ? Lock
                     : stage.state === "current" ? Sparkles
                     : Circle;
          // Surface a calm one-line "Almost there" hint when the latest
          // verdict is nearly_there or not_yet (carry-forward state).
          const verdict = stage?.confirmation?.verdict;
          const carryHint = verdict === "nearly_there"
            ? "Nearly there — one more drive to lock it in."
            : verdict === "not_yet"
            ? "Staying here — the next lesson tries a fresh angle."
            : null;
          return (
            <li
              key={stage.slug}
              data-testid={`stage-${stage.slug}`}
              data-stage-state={stage.state}
            >
              <button
                onClick={handleClick}
                disabled={isLocked}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition ${tone.bg} ${tone.text} ${isLocked ? "cursor-not-allowed" : "hover:bg-opacity-80 active:scale-[0.99]"} ring-1 ${tone.ring} ring-inset`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-none mt-0.5 ${tone.badge}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] uppercase tracking-widest font-bold opacity-70">
                      {STATE_LABEL[stage.state]}
                    </span>
                    {stage.manually_unlocked && (
                      <span className="text-[10px] uppercase tracking-widest font-bold rounded-full px-1.5 py-0.5 bg-purple-100 text-purple-700">
                        Instructor unlocked
                      </span>
                    )}
                  </div>
                  <div className={`text-sm font-bold leading-snug ${isLocked ? "" : ""}`}>
                    {stage.label}
                  </div>
                  {!isLocked && stage.lesson_count > 0 && (
                    <div className="text-[11px] mt-0.5 opacity-80">
                      {stage.completed_count} of {stage.lesson_count} lessons read
                    </div>
                  )}
                  {stage.state === "awaiting_confirmation" && (
                    <div data-testid={`stage-${stage.slug}-awaiting-banner`} className="text-[11px] mt-1 font-bold opacity-90">
                      All lessons read · awaiting instructor confirmation
                    </div>
                  )}
                  {carryHint && (
                    <div data-testid={`stage-${stage.slug}-carry-hint`} className="text-[11px] mt-1 italic opacity-90">
                      {carryHint}
                    </div>
                  )}
                  {isLocked && stage.unlock_requirement && (
                    <div className="text-[11px] mt-0.5 italic opacity-90">
                      {stage.unlock_requirement}
                    </div>
                  )}
                </div>
                {!isLocked && <ChevronRight className="w-4 h-4 flex-none mt-2 opacity-50" />}
              </button>
            </li>
          );
        })}
      </ol>
  );
}
